import { useState, useEffect, useRef } from 'react';
import api from '../lib/api';
import { toast } from 'sonner';
import { Paperclip, UploadSimple, Trash, DownloadSimple, File as FileIcon, ArrowsClockwise } from '@phosphor-icons/react';

const MAX_BYTES = 25 * 1024 * 1024;

function formatBytes(n) {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

function iconForMime(mime) {
  if (!mime) return FileIcon;
  return FileIcon; // keep simple — phosphor's File covers everything cleanly
}

export default function ContactFilesTab({ contactId }) {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const load = async () => {
    if (!contactId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/contacts/${contactId}/files`);
      setFiles(data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load files');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [contactId]);

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_BYTES) {
      toast.error(`File too large — max ${MAX_BYTES / 1024 / 1024} MB`);
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await api.post(`/contacts/${contactId}/files`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(`Uploaded "${file.name}"`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const onDownload = async (f) => {
    try {
      const resp = await api.get(`/contacts/files/${f.file_id}/download`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = f.filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error('Download failed');
    }
  };

  const onDelete = async (f) => {
    if (!window.confirm(`Delete "${f.filename}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/contacts/files/${f.file_id}`);
      toast.success('File deleted');
      load();
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  return (
    <div className="p-3 space-y-2" data-testid="tab-content-files">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-bold text-[#333] flex items-center gap-1.5">
          <Paperclip size={14} weight="bold" />
          Files ({files.length})
        </h3>
        <div>
          <input
            ref={inputRef}
            type="file"
            onChange={onUpload}
            className="hidden"
            data-testid="file-upload-input"
          />
          <button
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            className="px-2.5 py-1 bg-[#0B5394] hover:bg-[#073763] disabled:opacity-50 text-white text-[11px] font-semibold rounded flex items-center gap-1"
            data-testid="upload-file-btn"
          >
            {uploading ? <ArrowsClockwise size={12} className="animate-spin" /> : <UploadSimple size={12} weight="bold" />}
            {uploading ? 'Uploading…' : 'Upload File'}
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-xs text-[#888] py-4 text-center">Loading…</div>
      ) : files.length === 0 ? (
        <div className="text-xs text-[#888] py-6 text-center border border-dashed border-[#CCC] rounded">
          No files attached. Upload proposals, contracts, signed agreements, etc.
        </div>
      ) : (
        <ul className="divide-y divide-[#EEE] border border-[#DDD] rounded">
          {files.map(f => {
            const Icon = iconForMime(f.mime_type);
            return (
              <li key={f.file_id} className="flex items-center justify-between gap-2 px-3 py-2" data-testid={`file-${f.file_id}`}>
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  <Icon size={16} className="text-[#0B5394] shrink-0" weight="regular" />
                  <div className="min-w-0">
                    <div className="text-[12px] text-[#333] font-medium truncate">{f.filename}</div>
                    <div className="text-[10px] text-[#888]">
                      {formatBytes(f.size_bytes)} · uploaded {(f.uploaded_at || '').slice(0, 10)} by {f.uploaded_by_name || 'unknown'}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button
                    onClick={() => onDownload(f)}
                    className="px-2 py-1 text-[10px] border border-[#CCC] rounded hover:bg-[#F0F0F0] flex items-center gap-1"
                    data-testid={`file-download-${f.file_id}`}
                    title="Download"
                  >
                    <DownloadSimple size={11} />
                  </button>
                  <button
                    onClick={() => onDelete(f)}
                    className="px-2 py-1 text-[10px] border border-red-300 text-red-700 rounded hover:bg-red-50 flex items-center gap-1"
                    data-testid={`file-delete-${f.file_id}`}
                    title="Delete"
                  >
                    <Trash size={11} />
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
